import React, { useState } from 'react';
import { ArrowLeft, Loader2, Mail } from 'lucide-react';
import { useAuthStore } from '@/stores/authStore';
import { useTranslation } from '@/i18n';
import { clsx } from 'clsx';

interface ForgotPasswordPageProps {
  onBack: () => void;
}

export const ForgotPasswordPage: React.FC<ForgotPasswordPageProps> = ({ onBack }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const { forgotPassword } = useAuthStore();
  const { t } = useTranslation();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setLoading(true);

    try {
      const msg = await forgotPassword(email);
      setMessage(msg);
      setSent(true);
    } catch (err: any) {
      setError(err?.message || t.auth.forgotPasswordFailed);
    } finally {
      setLoading(false);
    }
  };

  if (sent) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 transition-colors">
        <div
          className={clsx(
            'w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700',
            'bg-white dark:bg-gray-800 shadow-lg p-8 text-center'
          )}
        >
          <div className="flex justify-center mb-4">
            <Mail className="h-12 w-12 text-blue-600 dark:text-blue-400" />
          </div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-3">
            {t.auth.checkYourEmail}
          </h2>
          <p className="text-gray-600 dark:text-gray-400 mb-6">
            {message}
          </p>
          <button
            onClick={onBack}
            className={clsx(
              'w-full flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm text-white',
              'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
              'transition-colors'
            )}
          >
            {t.auth.goToLogin}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50 dark:bg-gray-900 px-4 transition-colors">
      <div
        className={clsx(
          'w-full max-w-md rounded-xl border border-gray-200 dark:border-gray-700',
          'bg-white dark:bg-gray-800 shadow-lg p-8'
        )}
      >
        <div className="flex items-center mb-8">
          <button
            onClick={onBack}
            className="p-2 -ml-2 rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
          >
            <ArrowLeft className="h-5 w-5" />
          </button>
        </div>

        <h2 className="text-xl font-bold text-gray-900 dark:text-white text-center mb-2">
          {t.auth.forgotPasswordTitle}
        </h2>
        <p className="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
          {t.auth.forgotPasswordHint}
        </p>

        {error && (
          <div className="mb-4 px-4 py-3 rounded-lg bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              {t.auth.email}
            </label>
            <input
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
              className={clsx(
                'w-full px-3 py-2 rounded-lg border text-sm',
                'border-gray-300 dark:border-gray-600',
                'bg-white dark:bg-gray-700',
                'text-gray-900 dark:text-white',
                'focus:ring-2 focus:ring-blue-500 focus:border-transparent',
                'transition-colors'
              )}
              placeholder={t.auth.emailPlaceholder}
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className={clsx(
              'w-full flex items-center justify-center px-4 py-2.5 rounded-lg font-medium text-sm text-white',
              'bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600',
              'disabled:opacity-50 disabled:cursor-not-allowed',
              'transition-colors'
            )}
          >
            {loading ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t.common.loading}
              </>
            ) : (
              t.auth.sendResetLink
            )}
          </button>
        </form>
      </div>
    </div>
  );
};
